import { FC, useState } from 'react'

import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { useTiktokPrice } from 'src/Pages/Artist/TiktokSchedulePage/TiktokScheduleSettingsPage/helpers'
import { useGetTikTokCampaignBudgetRangeQuery } from 'src/Redux/artistPaymentSettingsApi'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { useAppSelector } from 'src/Hooks/redux'
import { getTiktokPrice } from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { APP } from 'src/Configs/App'
import { ScheduleBudgetInput } from 'src/Components/ScheduleBudgetInput'
import { PrimaryScheduleSubscription } from 'src/Components/PrimaryScheduleSubscription'
import { PrimarySlider } from 'src/Components/PrimarySlider'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'
import { LowBudgetAlert } from 'src/Components/LowBudgetAlert'

import { TiktokScheduleBudgetEstimation } from './TiktokScheduleBudgetEstimation'
import { TiktokScheduleContinueButton } from './TiktokScheduleContinueButton'
import { LOW_BUDGET_THRESHOLD, VIDEOS_BASED_SLIDER_STEP } from './helpers'
import { useLowBudgetAlert } from './useLowBudgetAlert'
import { useTiktokBudgetHandlers } from './useTiktokBudgetHandlers'

const Container = styled.div`
  display: flex;
  padding: 24px;
  margin-bottom: 70px;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};

  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin-bottom: 24px;
`

const BudgetSliderWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
`

const BudgetSliderSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-self: flex-start;
  min-height: 0;
  margin-bottom: 24px;
`

const BudgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SliderWithAnchor = styled.div`
  position: relative;
  display: flex;
`

const StyledSkeleton = styled(PrimarySkeleton)`
  width: 100%;
  height: 315px;
  border-radius: 16px;
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

export const TiktokScheduleBudget: FC = () => {
  const scheduledTikTokCampaign = useSelector(getScheduledTikTokCampaign)
  const campaignPricingModel = scheduledTikTokCampaign.pricingModel
  const campaignId = scheduledTikTokCampaign.id

  const { data } = useGetSubscriptionQuery()
  const hasActivePlan = Boolean(
    data?.status === 'active' || data?.status === 'canceled',
  )

  const { data: budgetRange } = useGetTikTokCampaignBudgetRangeQuery(
    campaignPricingModel ?? null,
  )

  const minBudget = Number(budgetRange?.campaignMinBudget ?? 0)
  const maxBudget = Number(budgetRange?.campaignMaxBudget ?? 0)

  const audiencePrice = useAppSelector(getTiktokPrice)
  const hasBudgetBounds = minBudget > 0 && maxBudget > 0
  const [hasInitialized, setHasInitialized] = useState(false)
  if (!hasInitialized && hasBudgetBounds && audiencePrice > 0) {
    setHasInitialized(true)
  }

  const {
    rate,
    currencySymbol,
    valueInput,
    valueSlider,
    setValueInput,
    setValueSlider,
    savePrice,
    pricingModel,
    loading,
  } = useTiktokPrice()

  const isViewsBasedModel =
    pricingModel === TikTokCampaignPricingModelEnum.ViewsBased

  const isVideosBasedModel =
    pricingModel === TikTokCampaignPricingModelEnum.VideosBased

  const sliderStep = isVideosBasedModel
    ? VIDEOS_BASED_SLIDER_STEP
    : APP.REACH_STEP_BUDGET_TIKTOK_CAMPAIGN

  const { isLowBudgetVisible, closeLowBudgetAlert } = useLowBudgetAlert({
    campaignId,
    isViewsBasedModel,
    valueSlider,
  })

  const {
    handleSliderChange,
    handleSliderChangeCompleted,
    handleInputChange,
    handleInputBlur,
  } = useTiktokBudgetHandlers({
    rate,
    currencySymbol,
    minBudget,
    valueInput,
    setValueInput,
    setValueSlider,
    savePrice,
  })

  if (!hasInitialized) {
    return <StyledSkeleton />
  }

  return (
    <Container>
      <Title>Set up campaign budget</Title>

      <BudgetSliderWrapper>
        <SliderWithAnchor>
          {isViewsBasedModel && (
            <LowBudgetAlert
              isLowBudgetVisible={isLowBudgetVisible}
              onCloseLowBudgetAlert={closeLowBudgetAlert}
            />
          )}

          <PrimarySlider
            min={minBudget}
            max={maxBudget}
            value={valueSlider}
            onChange={handleSliderChange}
            onChangeComplete={handleSliderChangeCompleted}
            step={sliderStep}
            formatValue={(val) =>
              `${currencySymbol}${Math.round(val * rate).toLocaleString(
                'en-US',
              )}`
            }
            disabled={loading}
            heightMode={!hasActivePlan}
            isViewsBasedModel={isViewsBasedModel}
            warningThreshold={LOW_BUDGET_THRESHOLD}
          />
        </SliderWithAnchor>

        <BudgetSliderSideContainer>
          <BudgetContainer>
            <ScheduleBudgetInput
              value={`${currencySymbol}${valueInput}`}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
            />
          </BudgetContainer>

          <PrimaryScheduleSubscription
            symbol={currencySymbol}
            price={valueInput}
          />

          <TiktokScheduleBudgetEstimation />
        </BudgetSliderSideContainer>
      </BudgetSliderWrapper>

      <TiktokScheduleContinueButton />
    </Container>
  )
}
